/*
 * Every delivery to every home on one page, so a box that arrived at one home and was never booked in
 * does not wait for someone to happen to open that home.
 *
 * Opens on the ones still waiting to be booked in, because those are the ones that need chasing.
 * Choosing one opens it inside its home, on the same screen the home books it in from.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from './api.ts';
import { moment, shortDay } from './days.ts';
import type { DeliveryShow, Go } from './pharmacy-routes.ts';

type Row = {
  id: string;
  homeId: string;
  homeName: string;
  kind: 'cycle' | 'interim';
  cycleStart: string | null;
  reference: string;
  status: 'draft' | 'sent' | 'received';
  createdAt: string;
  sentAt: string | null;
  bookedInAt: string | null;
  problems: number;
  lines: number;
  checked: number;
};

type Counts = Record<DeliveryShow, number>;

const VIEWS: [DeliveryShow, string][] = [
  ['waiting', 'Waiting to be booked in'],
  ['drafts', 'Being made up'],
  ['done', 'Booked in'],
  ['all', 'All'],
];

const EMPTY: Record<DeliveryShow, string> = {
  waiting: 'Nothing is waiting to be booked in. Every delivery that went out has been checked in.',
  drafts: 'No deliveries are being made up.',
  done: 'No deliveries have been booked in yet.',
  all: 'No deliveries yet. They are made up from inside each home, on its Deliveries tab.',
};

export function AllDeliveriesPage({ go, show }: { go: Go; show: DeliveryShow }) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [counts, setCounts] = useState<Counts | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ deliveries: Row[]; counts: Counts }>(`/api/pharmacy/deliveries?show=${show}`);
      setRows(data.deliveries);
      setCounts(data.counts);
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the deliveries.');
    }
  }, [show]);

  useEffect(() => {
    setRows(null);
    void load();
  }, [load]);

  return (
    <section>
      <div className="head">
        <div>
          <h1>Deliveries</h1>
          <p className="muted">Every delivery to every home.</p>
        </div>
        <button type="button" className="quiet" onClick={() => void load()}>Refresh</button>
      </div>

      <nav className="tabs" aria-label="Which deliveries">
        {VIEWS.map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={show === id ? 'on' : ''}
            onClick={() => go({ page: 'deliveries', show: id })}
          >
            {label}{counts ? ` (${counts[id]})` : ''}
          </button>
        ))}
      </nav>

      {error ? <p className="error" role="alert">{error}</p> : null}
      {rows === null ? <p className="muted">Loading…</p> : null}
      {rows && !rows.length ? <p className="muted">{EMPTY[show]}</p> : null}

      {rows && rows.length ? (
        <div className="scroll">
          <table className="deliveries">
            <thead>
              <tr>
                <th scope="col">Home</th>
                <th scope="col">Delivery</th>
                <th scope="col">Sent</th>
                <th scope="col">Checked</th>
                <th scope="col">Where it is</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  tabIndex={0}
                  onClick={() => go({ page: 'home', homeId: row.homeId, tab: 'deliveries', deliveryId: row.id })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      go({ page: 'home', homeId: row.homeId, tab: 'deliveries', deliveryId: row.id });
                    }
                  }}
                >
                  <td className="row-main">{row.homeName}</td>
                  <td>
                    {row.kind === 'interim' ? 'Interim' : row.cycleStart ? `Cycle from ${shortDay(row.cycleStart)}` : 'Cycle'}
                    {row.reference ? <span className="muted small"> · {row.reference}</span> : null}
                  </td>
                  <td className="nums">{row.sentAt ? moment(row.sentAt) : <span className="muted">Not yet</span>}</td>
                  <td className="nums">
                    {row.checked} of {row.lines}
                  </td>
                  <td>
                    <Status row={row} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

function Status({ row }: { row: Row }) {
  if (row.status === 'draft') return <span className="chip">Being made up</span>;
  if (row.status === 'sent') return <span className="chip warn">Waiting to be booked in</span>;
  return (
    <span className="chips">
      <span className="chip done">Booked in{row.bookedInAt ? ` ${moment(row.bookedInAt)}` : ''}</span>
      {row.problems ? <span className="chip off">{row.problems} problem{row.problems === 1 ? '' : 's'}</span> : null}
    </span>
  );
}
